import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import slides from "../data/slides";
import SlideSection from "../components/home/SlideSection";
import CircularTimer from "../components/home/CircularTimer";
import IntroMenu from "../components/home/IntroMenu";
import TypewriterIntro from "../components/home/TypewriterIntro";
import SlideNav from "../components/slide/SlideNav";
import SlideDots from "../components/slide/SlideDots";
import { ChevronDown, ChevronUp } from "lucide-react";

const DURATION = 10;
const STORY_VIEW_QUERY = "story";
const INTRO_VIEW_QUERY = "intro";
const STORY_ENTRY_STORAGE_KEY = "homeStoryEntered";
const TYPEWRITER_STORAGE_KEY = "typewriterShown";

const isStandaloneDisplayMode = () => {
  if (typeof window === "undefined") return false;

  const iosNavigator = window.navigator as Navigator & { standalone?: boolean };

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    iosNavigator.standalone === true
  );
};

const getRequestedHomeView = (search: string) => {
  const view = new URLSearchParams(search).get("view");
  return view === STORY_VIEW_QUERY || view === INTRO_VIEW_QUERY ? view : null;
};

const shouldStartInStory = (search: string) => {
  if (typeof window === "undefined") return false;

  const requestedView = getRequestedHomeView(search);
  if (requestedView === INTRO_VIEW_QUERY) return false;

  return requestedView === STORY_VIEW_QUERY || isStandaloneDisplayMode();
};

const Home = () => {
  const location = useLocation();
  const [showTypewriter, setShowTypewriter] = useState(() => {
    if (typeof window === "undefined") return false;

    if (shouldStartInStory(window.location.search)) {
      return false;
    }

    const seen = sessionStorage.getItem(TYPEWRITER_STORAGE_KEY);
    return !seen;
  });

  const [currentIndex, setCurrentIndex] = useState(-1); // -1 for intro
  const [isPaused, setIsPaused] = useState(false);
  const [timerKey, setTimerKey] = useState(0);
  const hasEnteredSlidesOnce = useRef(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasAppliedInitialViewRef = useRef(false);

  const scrollToSection = useCallback(
    (sectionIndex: number, behavior: ScrollBehavior = "smooth") => {
      const target = scrollContainerRef.current?.children[
        sectionIndex
      ] as HTMLElement;
      target?.scrollIntoView({ behavior });
    },
    [],
  );

  const scrollToMenu = useCallback(() => {
    scrollToSection(0);
    setCurrentIndex(-1);
    setIsPaused(true);
    setTimerKey((prev) => prev + 1);
  }, [scrollToSection]);

  const goToSlide = useCallback(
    (slideIndex: number, behavior: ScrollBehavior = "smooth") => {
      const sectionIndex = slideIndex + 1; // Slides start at section[1]
      scrollToSection(sectionIndex, behavior);
      setCurrentIndex(slideIndex);
      if (typeof window !== "undefined") {
        localStorage.setItem(STORY_ENTRY_STORAGE_KEY, "true");
      }
      hasEnteredSlidesOnce.current = true;
      setIsPaused(false);
      setTimerKey((prev) => prev + 1);
    },
    [scrollToSection],
  );

  const scrollToSlides = useCallback(() => goToSlide(0), [goToSlide]);
  const slideShellStyle = {
    borderColor: "var(--surface-border)",
    background:
      "radial-gradient(circle at top left, color-mix(in srgb, var(--surface-strong) 14%, transparent), transparent 36%), linear-gradient(145deg, color-mix(in srgb, var(--bg) 62%, black), color-mix(in srgb, var(--bg) 44%, black) 42%, color-mix(in srgb, var(--accent-strong) 26%, transparent) 100%)",
    boxShadow: "0 35px 120px rgba(0,0,0,0.28)",
  };
  const slideGlowStyle = {
    background:
      "radial-gradient(circle at 15% 20%, color-mix(in srgb, var(--accent) 16%, transparent), transparent 24%), radial-gradient(circle at 80% 85%, color-mix(in srgb, var(--primary) 22%, transparent), transparent 28%)",
  };

  // Typewriter → IntroMenu
  useEffect(() => {
    if (!showTypewriter) return;

    const timeout = setTimeout(() => {
      setShowTypewriter(false);
      sessionStorage.setItem(TYPEWRITER_STORAGE_KEY, "true");
      scrollToSection(0);
    }, 6000);

    return () => clearTimeout(timeout);
  }, [showTypewriter, scrollToSection]);

  useEffect(() => {
    if (showTypewriter) {
      hasAppliedInitialViewRef.current = false;
      return;
    }

    const requestedView = getRequestedHomeView(location.search);
    const enterStory = shouldStartInStory(location.search);

    if (!enterStory && requestedView !== INTRO_VIEW_QUERY) {
      hasAppliedInitialViewRef.current = false;
      return;
    }

    if (hasAppliedInitialViewRef.current) return;

    hasAppliedInitialViewRef.current = true;

    window.requestAnimationFrame(() => {
      if (enterStory) {
        goToSlide(0, "auto");
        return;
      }

      scrollToSection(0, "auto");
      setCurrentIndex(-1);
      setIsPaused(true);
      setTimerKey((prev) => prev + 1);
    });
  }, [goToSlide, location.search, scrollToSection, showTypewriter]);

  // Auto slide transition
  useEffect(() => {
    if (isPaused || showTypewriter || currentIndex === -1) return;

    const timeout = setTimeout(() => {
      if (currentIndex < slides.length - 1) {
        goToSlide(currentIndex + 1);
      }
    }, DURATION * 1000);

    return () => clearTimeout(timeout);
  }, [currentIndex, isPaused, showTypewriter, goToSlide]);

  // IntersectionObserver: Track current slide/index
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || showTypewriter) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const section = entry.target as HTMLElement;

          if (entry.isIntersecting) {
            if (section.dataset.slideindex !== undefined) {
              const index = Number(section.dataset.slideindex);
              setCurrentIndex(index);
              setTimerKey((prev) => prev + 1);
              if (!hasEnteredSlidesOnce.current) {
                hasEnteredSlidesOnce.current = true;
                setTimeout(() => setIsPaused(false), 50);
              }
            } else if (section.id === "intro-menu") {
              setCurrentIndex(-1);
              setIsPaused(true);
              setTimerKey((prev) => prev + 1);
            }
          }
        });
      },
      { threshold: 0.6 },
    );

    const sections = container.querySelectorAll("section, div");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [showTypewriter]);

  return (
    <main className="relative isolate w-full h-[100svh] overflow-hidden">
      <div
        ref={scrollContainerRef}
        className={`no-scrollbar h-[100svh] w-full snap-y snap-mandatory overscroll-y-none scroll-smooth touch-pan-y ${
          showTypewriter ? "overflow-hidden" : "overflow-y-auto"
        }`}
      >
        {/* Slide 0: Intro Menu or Typewriter */}
        <div id="intro-menu" className="relative min-h-[100svh] snap-start">
          {showTypewriter ? (
            <TypewriterIntro />
          ) : (
            <IntroMenu onScroll={scrollToSlides} />
          )}
        </div>

        {/* Slide 1+: Content Slides */}
        {slides.map((slide, index) => (
          <section
            key={index}
            data-slideindex={index}
            className="relative flex min-h-[100svh] w-full snap-start items-start justify-center overflow-hidden px-3 pt-[max(4.5rem,env(safe-area-inset-top))] pb-[max(6.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-6 md:px-10 md:items-center"
          >
            <div
              className="absolute inset-x-2 inset-y-2 rounded-[1.6rem] border sm:inset-x-6 sm:inset-y-6 sm:rounded-[2rem] md:rounded-[2.5rem]"
              style={slideShellStyle}
            />
            <div className="absolute inset-0" style={slideGlowStyle} />

            {index === currentIndex && (
              <CircularTimer
                key={timerKey}
                duration={DURATION}
                isActive
                isPaused={isPaused}
                onComplete={() => {
                  if (!isPaused && currentIndex < slides.length - 1) {
                    goToSlide(currentIndex + 1);
                  }
                }}
                onRestart={() => {
                  setIsPaused((prev) => !prev);
                  setTimerKey((prev) => prev + 1);
                }}
              />
            )}

            <div className="relative z-10 flex w-full flex-col items-start justify-start px-1 sm:px-3 md:items-center md:justify-center md:px-8">
              <SlideSection
                index={index}
                total={slides.length}
                title={slide.title}
                subtitle={slide.subtitle}
                text={slide.text}
                image={slide.image}
              />
            </div>

            {/* Chevrons */}
            {index > 0 && (
              <button
                onClick={() => goToSlide(index - 1)}
                className="absolute left-1/2 z-30 hidden -translate-x-1/2 rounded-full border border-white/15 bg-black/30 p-2 text-white/80 backdrop-blur-xl transition hover:bg-black/45 hover:text-white sm:block"
                style={{ top: "max(1rem, env(safe-area-inset-top))" }}
                aria-label="Go to previous slide"
              >
                <ChevronUp className="w-7 h-7 animate-bounce" />
              </button>
            )}
            {index < slides.length - 1 && (
              <button
                onClick={() => goToSlide(index + 1)}
                className="absolute left-1/2 z-30 hidden -translate-x-1/2 rounded-full border border-white/15 bg-black/30 p-2 text-white/80 backdrop-blur-xl transition hover:bg-black/45 hover:text-white sm:block"
                style={{ bottom: "max(1rem, env(safe-area-inset-bottom))" }}
                aria-label="Go to next slide"
              >
                <ChevronDown className="w-7 h-7 animate-bounce" />
              </button>
            )}
          </section>
        ))}
      </div>

      {!showTypewriter && currentIndex >= 0 && (
        <div className="pointer-events-none absolute inset-0 z-40">
          <div className="pointer-events-auto">
            <SlideNav onHomeClick={scrollToMenu} isVisible />
          </div>
          <div className="pointer-events-auto">
            <SlideDots
              total={slides.length}
              currentIndex={currentIndex}
              onDotClick={(index) => goToSlide(index)}
              onHomeClick={scrollToMenu}
            />
          </div>
        </div>
      )}
    </main>
  );
};

export default Home;
